import { useEffect, useState } from 'react';
import tinycolor from 'tinycolor2';

import {
  CHINESE_CHESTNUT_TILESET_NAME,
  SUITABILITY_THRESHOLD,
  SUITABILITY_THRESHOLD_FOR_CHESTNUTS,
  TILESET_MAX_ZOOM,
  TILESET_MIN_ZOOM,
} from '../constants/tilesets';
import { getGradientHueAtPosition } from '../utils/colors';
import { layerIdForTileset } from '../utils/tilesets';
import RasterLayerWithClipping from '../utils/webgl/RasterLayerWithClipping';

const SATURATION_THRESHOLD = 0.15; // Filter out grey & grey-ish colors

/**
 * When the map is loaded and the tileset data has loaded, add a
 * RasterLayerWithClipping layer to the map for each tileset.
 */
const useAddClippableTilesetLayers = (map, tilesets) => {
  // Keep track of the RasterLayerWithClipping instances
  const [tilesetLayers, setTilesetLayers] = useState([]);

  // Ensure we always have a raster layer for each tileset:
  useEffect(() => {
    if (!map) return null;

    // Create a raster layer for each tileset
    const newTilesetLayers = tilesets.map(
      ({ id, name, zoomLevels, continuousKey }) => {
        const maxSuitabilityColor = continuousKey.slice(-1)[0]?.hexCode;
        const maxSuitabilityHue =
          tinycolor(maxSuitabilityColor).toHsl().h / 360;

        let hueTarget = -1;
        if (name === CHINESE_CHESTNUT_TILESET_NAME && maxSuitabilityColor) {
          // hacky fix for the fact that chestnuts always have worse suitability than other crops:
          hueTarget = maxSuitabilityHue;
        }

        let hueThreshold = 0;
        if (maxSuitabilityHue) {
          const gradientColors = continuousKey.map(({ hexCode }) => hexCode);

          const suitabilityThreshold =
            // hacky fix for the fact that chestnuts always have worse suitability than other crops:
            name === CHINESE_CHESTNUT_TILESET_NAME
              ? SUITABILITY_THRESHOLD_FOR_CHESTNUTS
              : SUITABILITY_THRESHOLD;

          hueThreshold = getGradientHueAtPosition(
            suitabilityThreshold,
            gradientColors,
          );

          hueThreshold *= 1.0001; // Bump it up verrrry slightly to fix a floating point issue
        }

        return new RasterLayerWithClipping({
          id: layerIdForTileset(id),
          spec: {
            type: 'raster',
            tiles: [
              `${window.location.origin}/api/public/tileset/${id}/{z}/{x}/{y}.png`,
            ],
            tileSize: 256, // This matches TilesetLayer.jsx — if our tile size changes, this will need to be updated
            minzoom: zoomLevels ? Math.min(...zoomLevels) : TILESET_MIN_ZOOM,
            maxzoom: zoomLevels ? Math.max(...zoomLevels) : TILESET_MAX_ZOOM,
            scheme: 'tms',
          },
          opacity: 0, // initially render w/ no visibility, we set the selectedCrop layer to be visible later
          resampling: 'nearest',
          saturationThreshold: SATURATION_THRESHOLD, // Don't show grey areas on the map
          hueTarget,
          hueThreshold,
        });
      },
    );

    // add each layer to the map
    newTilesetLayers.forEach((layer) => {
      map.addLayer(layer);
    });

    // update state
    setTilesetLayers(newTilesetLayers);

    // cleanup function: remove all tileset layers
    return () => {
      if (!map || map._removed) return; // eslint-disable-line no-underscore-dangle

      newTilesetLayers.forEach((layer) => {
        map.removeLayer(layer.id);
      });
      setTilesetLayers([]);
    };
  }, [map, tilesets]);

  return tilesetLayers;
};

export default useAddClippableTilesetLayers;
